import { defineStore } from 'pinia'
import { api } from 'boot/receiver/axios'
import { isAxiosError } from 'axios'
import { Cookies } from 'quasar'

import { useErrorStore } from 'stores/common/error'

import checkSudoCode from 'composables/receiver/auth/checkSudoCode'

import type {
  PaginationPayload,
  SuccessApiRes,
  SuccessPaginationRes,
} from 'types/common/api'
import type { Pagination } from 'types/common/pagination'
import type {
  ManageTemplateMetadata,
  TemplateMetadata,
  TemplateReq,
  TemplateShop,
  TemplateType,
} from 'types/receiver/templates/templates'

export const useTemplateStore = defineStore('template', {
  state: () => ({
    templates: null as PaginationPayload<TemplateType> | null,
    editedTemplate: null as TemplateType | null,
    formatShop: null as TemplateShop | null,
    delimiters: [] as TemplateMetadata['delimiters'],
    enclosures: [] as TemplateMetadata['enclosures'],
    escapes: [] as TemplateMetadata['escapes'],
    encodings: [] as TemplateMetadata['encodings'],
    boms: [] as TemplateMetadata['boms'],
    newlines: [] as TemplateMetadata['newlines'],
    accessTypes: [] as TemplateMetadata['access_types'],
    organisationHandle: '',
    manageTemplateListPagination: null as null | {
      pagination: Pagination
      filter: string | null
    },
  }),

  actions: {
    async fetchTemplateMetaData() {
      try {
        const res = await api.get('/receive/template/metadata')

        const data: SuccessApiRes<TemplateMetadata> = res.data
        if (data.success) {
          this.formatShop = data.payload.shop
          this.boms = data.payload.boms
          this.delimiters = data.payload.delimiters
          this.enclosures = data.payload.enclosures
          this.encodings = data.payload.encodings
          this.escapes = data.payload.escapes
          this.newlines = data.payload.newlines
          this.organisationHandle = data.payload.organisation_handle
          this.accessTypes = data.payload.access_types
        }
        return data.success
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    async fetchListMetadata() {
      try {
        const res = await api.get('/receive/templates/metadata')

        const data: SuccessApiRes<ManageTemplateMetadata> = res.data

        if (data.success) {
          return data.payload
        } else return false
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    async fetchTemplates(
      page: number,
      per_page = 5,
      params?: { criteria?: string; template_id?: string }
    ) {
      try {
        const res = await api.get('/receive/templates', {
          params: {
            page,
            per_page,
            ...params,
          },
        })

        const data: SuccessPaginationRes<TemplateType> = res.data
        if (data.success) {
          return data.payload
        } else return false
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    async fetchTemplate(templateId: string) {
      try {
        const res = await api.get(`/receive/template/${templateId}`)

        const data: SuccessApiRes<TemplateType> = res.data

        if (data.success) {
          this.editedTemplate = data.payload
        }
        return data.payload
      } catch (error) {
        useErrorStore().updateBackErrors(error)

        return false
      }
    },

    async updateTemplate(templateId: string, template: Partial<TemplateReq>) {
      try {
        const res = await api.put(`/receive/template/${templateId}`, template)

        const data: SuccessApiRes<TemplateType> = res.data

        if (data.success) {
          return data.payload
        } else return false
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    async transferTemplate(
      templateId: string,
      transfer: { organisation_id: string; template_name: string }
    ) {
      try {
        const res = await api.put(
          `/receive/template/${templateId}/transfer`,
          transfer
        )

        return res.status >= 200 && res.status < 300
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    async checkHandleUniqueness(templateId: string, handle: string) {
      try {
        const res = await api.get(
          `/receive/template/${templateId}/check-handle`,
          {
            params: { handle },
          }
        )

        const data: SuccessApiRes<{ is_available: boolean }> = res.data

        if (data.success) {
          return { isUnique: data.payload.is_available }
        } else return false
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    // Update template picture
    async updateTemplatePicture(templateId: string, picture: File) {
      try {
        const formData = new FormData()
        formData.append('picture', picture)

        const res = await api.post(
          `/receive/template/${templateId}/picture`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        )
        const data: SuccessApiRes<TemplateType> = res.data

        if (data.success) {
          this.editedTemplate = data.payload
          return true
        } else return false
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    async deleteTemplate(templateId: string, template_name: string) {
      try {
        const res = await api.delete(`/receive/template/${templateId}`, {
          data: { template_name },
        })

        return res.status >= 200 && res.status < 300
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        checkSudoCode(error)
        return false
      }
    },

    async unDeleteTemplate(templateId: string) {
      try {
        const res = await api.put(`/receive/template/${templateId}/undelete`)

        return res.status >= 200 && res.status < 300
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    async deleteTemplatePicture(templateId: string) {
      try {
        const res = await api.delete(`/receive/template/${templateId}/picture`)
        const data: SuccessApiRes<TemplateType> = res.data

        if (data.success) {
          this.editedTemplate = data.payload
          return true
        } else return false
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    async duplicateTemplate(templateId: string) {
      try {
        const res = await api.post(`/receive/template/${templateId}/duplicate`)

        return res.status >= 200 && res.status < 300
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    async finalizeTemplate(secret: string, fileId?: string | null) {
      try {
        let url = `/receive/template/finalize/${secret}`
        if (fileId) {
          url += `/${fileId}`
        }
        const res = await api.post(url)

        const data: SuccessApiRes<TemplateType> = res.data

        if (data.success) {
          return true
        } else return false
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          Cookies.remove('savedDraft', {
            path: '/',
          })
          Cookies.remove('draftFileId', {
            path: '/',
          })
        }
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    async getTemplateInstructions(templateId: string) {
      try {
        const res = await api.get(`/receive/template/${templateId}/download`, {
          responseType: 'arraybuffer',
        })

        return res.data as ArrayBuffer
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    async getTrashedTemplates(
      page: number,
      per_page = 20,
      params?: { criteria?: string }
    ) {
      try {
        const res = await api.get('/receive/templates/trash', {
          params: {
            page,
            per_page,
            ...params,
          },
        })

        const data: SuccessPaginationRes<TemplateType> = res.data
        if (data.success) {
          return data.payload
        } else return false
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },
  },
})
